import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * Pannello che sale dal basso (bottom sheet) — il contenitore standard di
 * qualunque cosa, sul telefono, chiederebbe una finestra al centro dello
 * schermo: filtri, elenchi di sezioni, scelte, moduli corti.
 *
 * Perché dal basso e non al centro: il pollice arriva al bordo inferiore e non
 * al centro dello schermo. Un menu che scende dall'alto (il pattern del
 * desktop) sul telefono è fuori portata, e va toccato due volte.
 *
 * Si chiude in tre modi, tutti e tre attesi da chi usa un telefono:
 *  - trascinando giù la maniglia (o l'intestazione);
 *  - toccando fuori, sul velo;
 *  - con Esc (tastiera bluetooth, e ci serve per l'accessibilità).
 *
 * Sta in un portale su <body> perché .page ha un'animazione con transform, e
 * un antenato con transform diventa il riferimento di position:fixed: dentro
 * la pagina il pannello si ancorerebbe alla pagina invece che allo schermo.
 */
export function MobileSheet({
  open,
  onClose,
  title,
  children,
  footer,
  labelledBy,
}: {
  open: boolean;
  onClose: () => void;
  /** Titolo in cima, dentro l'intestazione trascinabile. Facoltativo. */
  title?: string;
  children: ReactNode;
  /** Riga di azioni fissa in fondo, sopra la safe area. Facoltativa. */
  footer?: ReactNode;
  labelledBy?: string;
}) {
  const panelRef = useRef<HTMLDivElement>(null);
  // Quanto il pannello è stato tirato giù col dito, in pixel. null = non si sta
  // trascinando, quindi comanda la transizione CSS e non il dito.
  const [drag, setDrag] = useState<number | null>(null);
  const startY = useRef(0);
  const startT = useRef(0);

  // Esc chiude. Il listener vive solo mentre il pannello è aperto.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Mentre il pannello è aperto la pagina sotto non deve scorrere: l'attributo
  // su <body> lo blocca da CSS (mobile.css), senza toccare gli stili inline.
  useEffect(() => {
    if (!open) return;
    document.body.dataset.sheet = 'open';
    return () => { delete document.body.dataset.sheet; };
  }, [open]);

  // A ogni apertura si riparte da zero, altrimenti il pannello ricompare già
  // mezzo trascinato giù dalla volta prima.
  useEffect(() => { if (open) setDrag(null); }, [open]);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    // Solo il dito/mouse principale, e mai partendo da un controllo: se si
    // tocca un bottone dentro l'intestazione, quello deve funzionare.
    if (!e.isPrimary) return;
    if ((e.target as HTMLElement).closest('button, a, input, select, textarea')) return;
    startY.current = e.clientY;
    startT.current = performance.now();
    setDrag(0);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (drag === null) return;
    // Solo verso il basso: tirare in su non allunga il pannello (elastico finto).
    setDrag(Math.max(0, e.clientY - startY.current));
  }, [drag]);

  const onPointerUp = useCallback((e: React.PointerEvent) => {
    if (drag === null) return;
    const dist = drag;
    const speed = dist / Math.max(1, performance.now() - startT.current); // px/ms
    setDrag(null);
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
    // Si chiude se è stato tirato giù abbastanza (oltre 96px) OPPURE se è stato
    // un gesto veloce anche corto: è il flick, ed è quello che la gente fa.
    if (dist > 96 || speed > 0.6) onClose();
  }, [drag, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="m-sheet-root" role="dialog" aria-modal="true" aria-labelledby={labelledBy}>
      <div className="m-sheet-veil" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        className="m-sheet"
        // Mentre il dito trascina niente transizione, se no il pannello insegue
        // il dito con un ritardo e sembra rotto.
        style={drag !== null ? { transform: `translateY(${drag}px)`, transition: 'none' } : undefined}
      >
        <div
          className="m-sheet-grab"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          <span className="m-sheet-bar" aria-hidden="true" />
          {title && <div className="m-sheet-title">{title}</div>}
        </div>
        <div className="m-sheet-bd">{children}</div>
        {footer && <div className="m-sheet-ft">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
}
